import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import ModeratorLayout from "@/components/ModeratorLayout/ModeratorLayout";
import {
  getModeratorsMeProfileRetrieveQueryKey,
  useModeratorsMeProfileChangePasswordCreate,
  useModeratorsMeProfilePartialUpdate,
  useModeratorsMeProfileRetrieve,
  useModeratorsMeTwoFactorDisableCreate,
  useModeratorsMeTwoFactorEnableCreate,
  useModeratorsMeTwoFactorRecoveryCodesRegenerateCreate,
  useModeratorsMeTwoFactorSetupCreate,
} from "@/services/moderators/generated/moderators-profile/moderators-profile";
import "./ModeratorPage.css";
import "./ModeratorProfilePage.css";

const ModeratorProfilePage = () => (
  <ModeratorLayout>
    <ProfileContent />
  </ModeratorLayout>
);

const ProfileContent = () => {
  const { t } = useTranslation(["moderator", "common"]);
  const qc = useQueryClient();
  const { data: profile, isLoading, error } = useModeratorsMeProfileRetrieve();
  const invalidate = () => qc.invalidateQueries({ queryKey: getModeratorsMeProfileRetrieveQueryKey() });

  const updateMutation = useModeratorsMeProfilePartialUpdate({
    mutation: { onSuccess: invalidate },
  });
  const passwordMutation = useModeratorsMeProfileChangePasswordCreate();

  const [fullName, setFullName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [profileMessage, setProfileMessage] = useState<string | null>(null);

  useEffect(() => {
    if (profile) {
      setFullName(profile.fullName || "");
      setPhoneNumber(profile.phoneNumber || "");
    }
  }, [profile]);

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setProfileMessage(null);
    updateMutation.mutate(
      {
        data: {
          fullName: fullName.trim(),
          phoneNumber: phoneNumber.trim(),
        },
      },
      {
        onSuccess: () => setProfileMessage(t("profile.updated")),
        onError: (err) => setProfileMessage(extractError(err, t("shared.updateError"))),
      },
    );
  };

  if (isLoading) return <div className="mod-page__loading">{t("common:states.loading")}</div>;
  if (error || !profile) return <div className="mod-page__error">{t("profile.loadError")}</div>;

  const isSuper = profile.role === "SUPERMODERATOR";

  return (
    <div>
      <header className="mod-page__header">
        <div>
          <h1 className="mod-page__title">{t("profile.title")}</h1>
          <p className="mod-page__subtitle">{t("profile.subtitle")}</p>
        </div>
      </header>

      <section className="mod-page__card">
        <h2 className="mod-page__card-title">{t("profile.identity")}</h2>
        <p className="mod-page__card-meta">
          <span className={`mod-page__chip ${isSuper ? "mod-page__chip--success" : ""}`}>
            {isSuper ? t("shared.roleSuperModerator") : t("shared.roleModerator")}
          </span>
          <span className={`mod-page__chip ${profile.isActive ? "mod-page__chip--success" : "mod-page__chip--danger"}`}>
            {profile.isActive ? t("shared.active") : t("shared.inactive")}
          </span>
          <span className="mod-page__chip">{profile.email}</span>
        </p>

        <form className="mod-profile__form" onSubmit={handleProfileSubmit}>
          <label>
            {t("shared.fullName")}
            <input
              className="mod-page__input"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
            />
          </label>
          <label>
            {t("shared.phone")}
            <input
              className="mod-page__input"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder={t("shared.optionalPlaceholder")}
            />
          </label>
          <div className="mod-page__form-row">
            <button type="submit" className="mod-page__btn mod-page__btn--primary" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? t("common:actions.saving") : t("profile.save")}
            </button>
            {profileMessage ? (
              <span className={profileMessage === t("profile.updated") ? "mod-profile__ok" : "mod-page__inline-error"}>
                {profileMessage}
              </span>
            ) : null}
          </div>
        </form>
      </section>

      <ChangePasswordForm
        onSubmit={(payload) => passwordMutation.mutate({ data: payload })}
        isSubmitting={passwordMutation.isPending}
        error={
          passwordMutation.isError ? extractError(passwordMutation.error, t("profile.password.changeError")) : null
        }
        success={passwordMutation.isSuccess}
      />

      <TwoFactorSection enabled={Boolean(profile.twoFactorEnabled)} onMutated={invalidate} />
    </div>
  );
};

interface TwoFactorSectionProps {
  enabled: boolean;
  /** Invalidate the moderator profile query so the UI mirrors the new
   * `twoFactorEnabled` flag immediately after enable / disable. */
  onMutated: () => void;
}

/** Inline replacement for the user-side TwoFactorModal — same flow,
 * but rendered as a card section to match the rest of the moderator
 * profile page (which doesn't use modals). */
const TwoFactorSection = ({ enabled, onMutated }: TwoFactorSectionProps) => {
  // View modeled as an explicit state machine — see TwoFactorModal for
  // the rationale (recovery codes must only render right after issue).
  type View = "idle" | "verify-enable" | "show-codes" | "disable" | "regenerate";
  const { t } = useTranslation(["moderator", "common"]);
  const [view, setView] = useState<View>("idle");

  const [secret, setSecret] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [enableCode, setEnableCode] = useState("");
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [regenerateCode, setRegenerateCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const setupMutation = useModeratorsMeTwoFactorSetupCreate();
  const enableMutation = useModeratorsMeTwoFactorEnableCreate();
  const disableMutation = useModeratorsMeTwoFactorDisableCreate();
  const regenerateMutation = useModeratorsMeTwoFactorRecoveryCodesRegenerateCreate();

  const reset = () => {
    setView("idle");
    setSecret(null);
    setQrCode(null);
    setEnableCode("");
    setDisablePassword("");
    setDisableCode("");
    setRegenerateCode("");
    setRecoveryCodes([]);
    setError(null);
  };

  const beginSetup = () => {
    setError(null);
    // Setup takes no body — the orval mutation variables type is `void`.
    setupMutation.mutate(undefined, {
      onSuccess: (data) => {
        // Bridge `void` → real shape: the response is declared
        // untyped in the OpenAPI schema, but the runtime payload is
        // `{ secret, qrCode, provisioningUri }`.
        const d = data as unknown as { secret?: string; qrCode?: string };
        setSecret(d?.secret ?? null);
        setQrCode(d?.qrCode ?? null);
        setView("verify-enable");
      },
      onError: (err) => setError(extractError(err, t("profile.twoFactor.setupError"))),
    });
  };

  const submitEnable = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    enableMutation.mutate(
      { data: { code: enableCode.trim() } },
      {
        onSuccess: (data) => {
          const d = data as unknown as { recoveryCodes?: string[] };
          setRecoveryCodes(d?.recoveryCodes ?? []);
          setView("show-codes");
          onMutated();
        },
        onError: (err) => setError(extractError(err, t("profile.twoFactor.invalidCode"))),
      },
    );
  };

  const submitDisable = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    disableMutation.mutate(
      { data: { password: disablePassword, code: disableCode.trim() } },
      {
        onSuccess: () => {
          onMutated();
          reset();
        },
        onError: (err) => setError(extractError(err, t("profile.twoFactor.invalidPasswordOrCode"))),
      },
    );
  };

  const submitRegenerate = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    regenerateMutation.mutate(
      { data: { code: regenerateCode.trim() } },
      {
        onSuccess: (data) => {
          const d = data as unknown as { recoveryCodes?: string[] };
          setRecoveryCodes(d?.recoveryCodes ?? []);
          setView("show-codes");
          onMutated();
        },
        onError: (err) => setError(extractError(err, t("profile.twoFactor.invalidCode"))),
      },
    );
  };

  const downloadCodes = () => {
    const blob = new Blob([recoveryCodes.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "matchpoint-moderator-recovery-codes.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
    } catch {
      /* no-op — clipboard may be unavailable */
    }
  };

  return (
    <section className="mod-page__card">
      <h2 className="mod-page__card-title">{t("profile.twoFactor.title")}</h2>

      {view === "idle" && (
        <div className="mod-profile__form">
          <p className="mod-page__card-meta">
            <span className={`mod-page__chip ${enabled ? "mod-page__chip--success" : ""}`}>
              {enabled ? t("profile.twoFactor.enabled") : t("profile.twoFactor.disabled")}
            </span>
          </p>
          {!enabled && <p>{t("profile.twoFactor.intro")}</p>}
          <div className="mod-page__form-row">
            {enabled ? (
              <>
                <button
                  type="button"
                  className="mod-page__btn mod-page__btn--primary"
                  onClick={() => setView("regenerate")}
                >
                  {t("profile.twoFactor.regenerate")}
                </button>
                <button
                  type="button"
                  className="mod-page__btn mod-page__btn--danger"
                  onClick={() => setView("disable")}
                >
                  {t("profile.twoFactor.disable")}
                </button>
              </>
            ) : (
              <button
                type="button"
                className="mod-page__btn mod-page__btn--primary"
                onClick={beginSetup}
                disabled={setupMutation.isPending}
              >
                {setupMutation.isPending ? t("profile.twoFactor.preparing") : t("profile.twoFactor.enable")}
              </button>
            )}
            {error ? <span className="mod-page__inline-error">{error}</span> : null}
          </div>
        </div>
      )}

      {view === "verify-enable" && (
        <form className="mod-profile__form" onSubmit={submitEnable}>
          {qrCode && (
            <img
              src={qrCode}
              alt={t("profile.twoFactor.qrAlt")}
              style={{ width: 200, height: 200, alignSelf: "center" }}
            />
          )}
          {secret && (
            <p>
              <Trans t={t} i18nKey="profile.twoFactor.manualKey" values={{ secret }} components={{ code: <code /> }} />
            </p>
          )}
          <label>
            {t("profile.twoFactor.sixDigitCode")}
            <input
              className="mod-page__input"
              type="text"
              inputMode="numeric"
              maxLength={6}
              autoComplete="one-time-code"
              value={enableCode}
              onChange={(e) => setEnableCode(e.target.value)}
              required
            />
          </label>
          <div className="mod-page__form-row">
            <button type="submit" className="mod-page__btn mod-page__btn--primary" disabled={enableMutation.isPending}>
              {enableMutation.isPending ? t("profile.twoFactor.enabling") : t("profile.twoFactor.enable")}
            </button>
            <button type="button" className="mod-page__btn" onClick={reset} disabled={enableMutation.isPending}>
              {t("common:actions.cancel")}
            </button>
            {error ? <span className="mod-page__inline-error">{error}</span> : null}
          </div>
        </form>
      )}

      {view === "show-codes" && (
        <div className="mod-profile__form">
          <p>
            <Trans t={t} i18nKey="profile.twoFactor.saveCodes" components={{ strong: <strong /> }} />
          </p>
          <ul
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 8,
              padding: 12,
              background: "#f3f4f6",
              borderRadius: 8,
              listStyle: "none",
              margin: 0,
            }}
          >
            {recoveryCodes.map((c) => (
              <li key={c} style={{ fontFamily: "ui-monospace, monospace" }}>
                <code>{c}</code>
              </li>
            ))}
          </ul>
          <div className="mod-page__form-row">
            <button type="button" className="mod-page__btn" onClick={copyCodes}>
              {t("profile.twoFactor.copy")}
            </button>
            <button type="button" className="mod-page__btn" onClick={downloadCodes}>
              {t("profile.twoFactor.downloadTxt")}
            </button>
            <button type="button" className="mod-page__btn mod-page__btn--primary" onClick={reset}>
              {t("profile.twoFactor.savedThem")}
            </button>
          </div>
        </div>
      )}

      {view === "disable" && (
        <form className="mod-profile__form" onSubmit={submitDisable}>
          <p>{t("profile.twoFactor.disableIntro")}</p>
          <label>
            {t("profile.twoFactor.password")}
            <input
              type="password"
              className="mod-page__input"
              autoComplete="current-password"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
              required
            />
          </label>
          <label>
            {t("profile.twoFactor.authCode")}
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              className="mod-page__input"
              autoComplete="one-time-code"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value)}
              required
            />
          </label>
          <div className="mod-page__form-row">
            <button type="submit" className="mod-page__btn mod-page__btn--danger" disabled={disableMutation.isPending}>
              {disableMutation.isPending ? t("profile.twoFactor.disabling") : t("profile.twoFactor.disable")}
            </button>
            <button type="button" className="mod-page__btn" onClick={reset} disabled={disableMutation.isPending}>
              {t("common:actions.cancel")}
            </button>
            {error ? <span className="mod-page__inline-error">{error}</span> : null}
          </div>
        </form>
      )}

      {view === "regenerate" && (
        <form className="mod-profile__form" onSubmit={submitRegenerate}>
          <p>{t("profile.twoFactor.regenerateIntro")}</p>
          <label>
            {t("profile.twoFactor.authCode")}
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              className="mod-page__input"
              autoComplete="one-time-code"
              value={regenerateCode}
              onChange={(e) => setRegenerateCode(e.target.value)}
              required
            />
          </label>
          <div className="mod-page__form-row">
            <button
              type="submit"
              className="mod-page__btn mod-page__btn--primary"
              disabled={regenerateMutation.isPending}
            >
              {regenerateMutation.isPending ? t("profile.twoFactor.generating") : t("profile.twoFactor.generateCodes")}
            </button>
            <button type="button" className="mod-page__btn" onClick={reset} disabled={regenerateMutation.isPending}>
              {t("common:actions.cancel")}
            </button>
            {error ? <span className="mod-page__inline-error">{error}</span> : null}
          </div>
        </form>
      )}
    </section>
  );
};

interface PasswordFormProps {
  onSubmit: (payload: { currentPassword: string; newPassword: string }) => void;
  isSubmitting: boolean;
  error: string | null;
  success: boolean;
}

const ChangePasswordForm = ({ onSubmit, isSubmitting, error, success }: PasswordFormProps) => {
  const { t } = useTranslation(["moderator", "common"]);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const handle = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (next.length < 8) {
      setLocalError(t("profile.password.tooShort"));
      return;
    }
    if (next !== confirm) {
      setLocalError(t("profile.password.mismatch"));
      return;
    }
    onSubmit({ currentPassword: current, newPassword: next });
    setCurrent("");
    setNext("");
    setConfirm("");
  };

  return (
    <section className="mod-page__card">
      <h2 className="mod-page__card-title">{t("profile.password.title")}</h2>
      <form className="mod-profile__form" onSubmit={handle}>
        <label>
          {t("profile.password.current")}
          <input
            type="password"
            className="mod-page__input"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
            autoComplete="current-password"
          />
        </label>
        <label>
          {t("profile.password.new")}
          <input
            type="password"
            className="mod-page__input"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
          />
        </label>
        <label>
          {t("profile.password.confirm")}
          <input
            type="password"
            className="mod-page__input"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
          />
        </label>
        <div className="mod-page__form-row">
          <button
            type="submit"
            className="mod-page__btn mod-page__btn--primary"
            disabled={isSubmitting || !current || !next || !confirm}
          >
            {isSubmitting ? t("profile.password.changing") : t("profile.password.title")}
          </button>
          {localError || error ? <span className="mod-page__inline-error">{localError ?? error}</span> : null}
          {success && !localError && !error ? (
            <span className="mod-profile__ok">{t("profile.password.updated")}</span>
          ) : null}
        </div>
      </form>
    </section>
  );
};

function extractError(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: Record<string, unknown> } } | null | undefined;
  const body = e?.response?.data;
  if (body) {
    if (typeof body.error === "string") return body.error;
    for (const v of Object.values(body)) {
      if (typeof v === "string") return v;
      if (Array.isArray(v) && typeof v[0] === "string") return v[0];
    }
  }
  return fallback;
}

export default ModeratorProfilePage;
